import type { ExternalTableSchema } from '@packages/table-builder';

export const optionAwardApprovalTable: ExternalTableSchema = {
  metadata: {
    title: 'Option Awards Approval',
    entity: 'Option Award Approval',
  },

  dataSource: {
    endpoint: '/api/award/api/app/option-award-approval/option-award-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
      ApprovalStatusIds: 'filters.approvalStatusId',
      CreatedFromDate: '',
      CreatedToDate: '',
      GrantFromDate: '',
      GrantToDate: '',
      EmployeeStatuses: '',
      OptionStatus: '',
      OptionAcceptance: 'filters.optionAcceptance',
      IsEarlyExercisable: '',
      ApprovedFromDate: '',
      ApprovedToDate: '',
      PublishFromDate: '',
      PublishToDate: '',
      ExpirationFromDate: '',
      ExpirationToDate: '',
    },
    staticQueryParams: {
      OptionStatus: 0,
    },
    staticQueryParams: {
      OptionStatus: 0,
      EmployeeStatuses: [0],
    },
    filters: [
      {
        name: 'ApprovalStatusIds',
        placeholder: 'Approval Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/option-award/option-award-statuses',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'OptionAcceptance',
        placeholder: 'Accept Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award/api/app/option-award/option-acceptances',
          labelField: 'name',
          valueField: 'id',
        },
      },
    ],
  },

  columns: [
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      width: 200,
      couple: { titleField: 'participantName', subtitleField: 'employeeId' },
    },
    { field: 'grantNo', header: 'Award #', type: 'text', sortable: true, width: 180 },
    { field: 'grantName', header: 'Grant Name', type: 'text', sortable: true, width: 180 },
    {
      field: 'awardType',
      header: 'Award Type',
      type: 'text',
      sortable: false,
      width: 100,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'awardStatus',
      header: 'Award Status',
      type: 'badge',
      sortable: false,
      width: 100,
      align: 'center',
      visibility: 'picker',
      badge: {
        statusMap: {
          'Fully Vested': 'success',
          Expired: 'error',
          Rejected: 'error',
          Cancelled: 'error',
          Outstanding: 'info',
          Pending: 'info',
        },
        defaultVariant: 'info',
      },
    },
    {
      field: 'status',
      header: 'Approval Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      badge: {
        statusMap: { Approved: 'success', Cancelled: 'error', Rejected: 'error' },
        defaultVariant: 'info',
      },
    },
    {
      field: 'exercisePrice',
      header: 'Exercise Price',
      type: 'currency',
      sortable: false,
      width: 150,
      align: 'center',
      visibility: 'picker',
      format: { decimals: 2 },
    },
    { field: 'exercisableUnits', header: 'Exercisable Units', type: 'number', sortable: false, width: 150, visibility: 'picker' },
    { field: 'exercisedUnit', header: 'Exercise Unit', type: 'number', sortable: false, width: 150, visibility: 'picker' },
    { field: 'vestedUnits', header: 'Vested Units', type: 'number', sortable: false, width: 150, align: 'center', visibility: 'picker' },
    { field: 'unvestedUnit', header: 'Unvested Units', type: 'number', sortable: false, width: 150, align: 'center', visibility: 'picker' },
    { field: 'unitsGranted', header: 'Awarded Units', type: 'number', sortable: true, width: 150, align: 'center' },
    { field: 'issueDate', header: 'Grant Date', type: 'date', sortable: true, width: 180, align: 'center' },
    { field: 'creationTime', header: 'Created Date', type: 'date', sortable: false, width: 180, align: 'center', visibility: 'picker' },
    { field: 'vestingSchedule', header: 'Vesting Schedule', type: 'text', sortable: true, width: 180, align: 'center' },
    {
      field: 'outstandingPrice',
      header: 'Outstanding Value',
      type: 'text',
      sortable: false,
      width: 150,
      align: 'center',
      visibility: 'picker',
      format: { commas: true, fixed: true },
      fields: { formatAsNumber: 'true' },
    },
    {
      field: 'isAccepted',
      header: 'Accept Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      conditionalValues: { true: 'ACCEPTED', false: 'NEEDS ACCEPTANCE' },
      badge: { statusMap: { true: 'success', false: 'error' }, defaultVariant: 'info' },
    },
    {
      field: 'employeeStatus',
      header: 'Employee Status',
      type: 'badge',
      sortable: false,
      width: 180,
      align: 'center',
      visibility: 'picker',
      badge: { statusMap: { ACTIVE: 'success', INACTIVE: 'info' }, defaultVariant: 'info' },
    },
  ],

  actions: {
    row: [
      { id: 'VIEW_OPTION_AWARD_APPROVAL', label: 'View' },
      { id: 'APPROVE_OPTION_AWARD_APPROVAL', label: 'Approve' },
      { id: 'REJECT_OPTION_AWARD_APPROVAL', label: 'Reject', variant: 'danger' },
      { id: 'VIEW_COMMENTS_OPTION_AWARD_APPROVAL', label: 'View Comments' },
    ],
    bulk: [
      { id: 'BULK_APPROVE_OPTION_AWARD_APPROVAL', label: 'Approve' },
      { id: 'BULK_REJECT_OPTION_AWARD_APPROVAL', label: 'Reject', variant: 'danger' },
    ],
    toolbar: {},
  },

  features: {
    search: { enabled: true, placeholder: 'Search', debounce: 500 },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    selection: {
      keyField: 'id',
      selectable: { field: 'status', operator: 'EQUAL', value: 'Pending' },
    },
    columnPreferences: { persistenceKey: 'Option Award Approval', resizable: true, reorderable: true },
  },
};
